import { NotificationType } from './enum/notificationType.enum.js';
/**
 * Whether a notification still awaits its recipient, or merely told them
 * something (PRD #1453).
 *
 * This is the single policy the Posteingang consults to decide how a
 * notification leaves the Offen lane. It is asked per **type**, never per row:
 * an actionable notification that happens to share a fetched page with an
 * informational one is untouched.
 */
export var NotificationDisposition;
(function (NotificationDisposition) {
    /**
     * An announcement. Having been told *is* completion, so the row leaves Offen
     * as soon as an Offen fetch has shown it to the recipient.
     */
    NotificationDisposition["INFORMATIONAL"] = "INFORMATIONAL";
    /**
     * Something still awaits the recipient. The row stays in Offen until its
     * completion event fires elsewhere in the app (RS-007) or the user archives it
     * by hand — being looked at is not an answer.
     */
    NotificationDisposition["ACTIONABLE"] = "ACTIONABLE";
})(NotificationDisposition || (NotificationDisposition = {}));
/**
 * Every notification type, classified.
 *
 * **The annotation is the guard.** A literal object under a *total*
 * `Record<NotificationType, …>` fails the build the moment a new type is added
 * without being classified. A `Partial<…>`, an index signature or a widened
 * value type would accept the gap silently — do not loosen it, and do not add a
 * runtime test that re-checks these keys, because the compiler already does it
 * for free.
 *
 * Types marked `@deprecated` on the enum are no longer dispatched, but rows of
 * theirs still exist and are still listed, so they are classified too.
 *
 * The default is INFORMATIONAL, and deliberately so: a type wrongly filed as
 * informational archives on sight and is still findable in Archiv, whereas a
 * type wrongly filed as actionable sits in Offen forever and re-creates the
 * never-emptying list this PRD exists to fix. ACTIONABLE is therefore reserved
 * for types that do one of three things: carry an in-app answer, ask the
 * recipient to do something the app can observe them doing, or point at content
 * the user has nowhere else to reach. Anything that merely reports is
 * INFORMATIONAL, however consequential it reads.
 *
 * One invariant to keep when adding a type: **every ACTIONABLE type must compose
 * into a banner** (`getNotificationContent` must not return null for it).
 * ACTIONABLE is what lets a row sit in Offen indefinitely, and the only way out
 * for one with no completion event is the Archivieren button on its banner — so
 * an actionable type the Posteingang cannot render is a badge the user has no way
 * to clear.
 */
export const NOTIFICATION_DISPOSITION = {
    // --- Actionable: an in-app answer is pending ------------------------------
    /**
     * The accept/decline buttons are the only in-app way to answer. RS-007
     * archives it on `appointment.participant-status-changed` and
     * `appointment.participant-withdrew` — see `notification-completion.ts`.
     */
    [NotificationType.APPOINTMENT_INVITE]: NotificationDisposition.ACTIONABLE,
    // --- Actionable: an exercise session is pending ---------------------------
    // The exercise family asks the patient to train, and training is a thing the
    // app watches happen: `training-session.completed` is the completion event
    // RS-007 subscribes to for all four (PRD #1453: "training session completed
    // for the exercise family"). Which of the four a given session answers is in
    // `notification-completion.ts` — three are scoped to the program the session
    // was on, the untied reminder is not.
    //
    // The two reminders below are the deliberate part. They are cron-driven, so
    // filing them informational would clear the nudge the moment the patient
    // glanced at the Posteingang — which is the opposite of a nudge, and is what
    // the PRD rules out: "I want the daily exercise reminder to leave Offen once I
    // have completed the session, so that yesterday's nudge is not still sitting
    // there today." The accepted consequence is that a patient who skips several
    // days accumulates a few rows until their next session clears them; the PRD
    // takes that on explicitly ("The daily exercise reminder stays a row, because
    // completion now archives it cleanly") and rules out a time-based fallback.
    [NotificationType.NEW_EXERCISE_PROGRAM]: NotificationDisposition.ACTIONABLE,
    [NotificationType.EXERCISE_PROGRAM_UPDATED]: NotificationDisposition.ACTIONABLE,
    [NotificationType.TODAYS_EXERCISE_SCHEDULE]: NotificationDisposition.ACTIONABLE,
    [NotificationType.EXERCISE_PROGRAM_COMPLETION_REMINDER]: NotificationDisposition.ACTIONABLE,
    // --- Actionable with no completion event to listen for --------------------
    // These three have no signal the app can act on, so they sit in Offen until
    // archived by hand. That is deliberate rather than an oversight: each one is
    // pointing at a destination the product is missing, and a timer that quietly
    // cleared the items most likely to need attention would hide the gap instead
    // of closing it (PRD #1453, RS-007).
    [NotificationType.CRITICAL_PROGRAM_VALUES]: NotificationDisposition.ACTIONABLE,
    [NotificationType.PROGRAM_EXPIRED]: NotificationDisposition.ACTIONABLE,
    [NotificationType.NEW_INVOICE_AVAILABLE]: NotificationDisposition.ACTIONABLE,
    // --- Actionable because the notification is the only evidence -------------
    // These three read as pure announcements, and one day they will be. Today the
    // patient has no invoice list anywhere in the product, so the notification and
    // its email are the only places the app admits the document exists — and they
    // carry no link to follow either. Archiving them on sight would file away the
    // only pointer the patient has before they could act on it. They flip to
    // INFORMATIONAL once patients have an invoice list to be sent to.
    //
    // Like the three above they have no completion event either — there is no
    // "the patient read their invoice" for RS-007 to subscribe to while there is
    // nowhere to read it — so they too stay in Offen until archived by hand.
    [NotificationType.PATIENT_INVOICE_ISSUED]: NotificationDisposition.ACTIONABLE,
    [NotificationType.PATIENT_INVOICE_CANCELLED]: NotificationDisposition.ACTIONABLE,
    [NotificationType.HONORARVEREINBARUNG_SENT]: NotificationDisposition.ACTIONABLE,
    // --- Informational: announcements to therapists ---------------------------
    [NotificationType.PATIENT_REGISTER]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.OFFICE_USER_REGISTER]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.PATIENT_HAND_OVER]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.PATIENT_REMOVED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_ACCEPTED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_DECLINED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_CANCELLED_BY_PATIENT]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.CALL_THERAPIST_HANDOVER_ACTIVE]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_THERAPIST]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.NEW_OFFICE_EXERCISE]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.NEW_PROGRAM_STATS]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.NEW_PRODUCT_ASSIGNMENT]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.PRODUCT_ASSIGNMENT_7_DAY_WARNING]: NotificationDisposition.INFORMATIONAL,
    // --- Informational: announcements to patients -----------------------------
    [NotificationType.CALL_BEGINNING_REMINDER]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.CALL_CANCELED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_ACCEPT_SUCCESS]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_DECLINE_SUCCESS]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.PATIENT_HAND_OVER_AS_PATIENT]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.CALL_THERAPIST_HANDOVER_PASSIVE_PATIENT]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.COMPLETED_EXERCISE_PROGRAM]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.APPOINTMENT_RESCHEDULED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.BOOKING_CONFIRMED]: NotificationDisposition.INFORMATIONAL,
    // The decline reason is carried on the row and exists nowhere else, which is
    // an argument for never *deleting* it — not for keeping it in the queue.
    // Archiv is where it stays findable.
    [NotificationType.BOOKING_DECLINED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.BOOKING_AUTO_CANCELLED]: NotificationDisposition.INFORMATIONAL,
    // --- Informational: system announcements ----------------------------------
    // None of these three composes into a banner (`getNotificationContent`
    // returns null), so a user whose Offen lane held only these would see a badge
    // over a Posteingang that renders nothing. Informational is what lets the
    // fetch clear them.
    [NotificationType.SYSTEM_USER_SESSIONS_ENDED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.SYSTEM_USER_STRIPE_ENTITLEMENTS_UPDATED]: NotificationDisposition.INFORMATIONAL,
    [NotificationType.SYSTEM_USER_STRIPE_PAYOUT_FAILED]: NotificationDisposition.INFORMATIONAL,
};
/**
 * Whether being shown this notification is the whole of it — the question an
 * Offen fetch asks before filing the rows it just handed out.
 */
export const isInformationalNotification = (type) => NOTIFICATION_DISPOSITION[type] === NotificationDisposition.INFORMATIONAL;
//# sourceMappingURL=notification-disposition.js.map